import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { PackageCheck } from 'lucide-react'
import { useState, type ReactNode } from 'react'

import {
  Badge,
  Button,
  EmptyState,
  Field,
  InlineAlert,
  Input,
  Section,
  Spinner,
  SwitchField,
  Textarea,
  type StatusTone,
} from '@aeris/ui'

import { TabHeader } from '../components/config-tabs.tsx'
import { WorkingHoursEditor } from '../components/working-hours-editor.tsx'
import {
  ApiError,
  cancelSaleMessage,
  fetchSaleMessageConfig,
  fetchSaleMessages,
  saveSaleMessageConfig,
  sendSaleMessageNow,
  type SaleMessageConfigType,
  type SaleMessageEntryType,
} from '../lib/api.ts'
import { useAuth } from '../lib/auth-context.ts'

/**
 * Atendimento → Configuração → Pós-venda (F245/F246).
 *
 * O que a venda manda ao cliente pelo número de quem vendeu:
 *
 * - **"Saiu para entrega":** quando o caixa marca a saída com o motoboy;
 * - **"Deu certo a instalação?":** horas depois da compra, só com a loja
 *   aberta. "Deu certo" recebe a oferta; "não funcionou" ganha a etiqueta
 *   "Pós-venda: problema" e vai para a vendedora, sem resposta automática.
 *
 * Em cima, o que saiu (ou vai sair) no dia; embaixo, a configuração.
 */

const KIND_LABELS: Record<string, string> = {
  dispatched: 'Saiu para entrega',
  aftersale_check: 'Deu certo?',
  aftersale_offer: 'Oferta',
}

const SKIP_LABELS: Record<string, string> = {
  no_conversation: 'o cliente nunca conversou nestes números',
  outside_window: 'fora das 24h do número oficial',
  asked_today: 'o cliente já foi perguntado hoje',
  disabled: 'estava desligado',
  sale_not_found: 'venda não encontrada',
}

const statusOf = (
  entry: SaleMessageEntryType
): { label: string; tone: StatusTone } => {
  switch (entry.status) {
    case 'scheduled':
      return { label: 'Agendada', tone: 'info' }
    case 'sent':
      return { label: 'Enviada', tone: 'neutral' }
    case 'cancelled':
      return { label: 'Cancelada', tone: 'neutral' }
    case 'skipped':
      return { label: 'Não saiu', tone: 'warning' }
    case 'answered':
      return entry.replyClass === 'worked'
        ? { label: 'Deu certo', tone: 'success' }
        : entry.replyClass === 'problem'
          ? { label: 'Problema', tone: 'danger' }
          : { label: 'Respondeu', tone: 'info' }
  }
}

const timeOf = (iso: string): string =>
  new Intl.DateTimeFormat('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso))

const today = (): string =>
  new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo' }).format(
    new Date()
  )

const DayList = ({ canManage }: { canManage: boolean }): ReactNode => {
  const queryClient = useQueryClient()
  const [date, setDate] = useState(today)
  const [error, setError] = useState<string | null>(null)
  const entries = useQuery({
    queryKey: ['sale-messages', date],
    queryFn: async ({ signal }) => await fetchSaleMessages(date, signal),
  })
  const act = useMutation({
    mutationFn: async (input: { id: string; action: 'cancel' | 'now' }) => {
      if (input.action === 'cancel') await cancelSaleMessage(input.id)
      else await sendSaleMessageNow(input.id)
    },
    onSuccess: async () => {
      setError(null)
      await queryClient.invalidateQueries({ queryKey: ['sale-messages'] })
    },
    onError: (cause: unknown) => {
      setError(
        cause instanceof ApiError ? cause.message : 'Não foi possível agora.'
      )
    },
  })

  const rows = entries.data ?? []
  return (
    <Section
      title="O que saiu"
      description="As mensagens do dia, com a hora em que saíram ou vão sair."
    >
      <Field label="Dia" className="sm:w-48">
        {(field) => (
          <Input
            {...field}
            type="date"
            value={date}
            onChange={(event) => {
              if (event.target.value !== '') setDate(event.target.value)
            }}
          />
        )}
      </Field>
      {error === null ? null : (
        <InlineAlert tone="danger" className="mt-3">
          {error}
        </InlineAlert>
      )}
      {entries.isPending ? (
        <div className="flex justify-center py-6">
          <Spinner />
        </div>
      ) : rows.length === 0 ? (
        <p className="mt-3 text-(length:--text-sm) text-(--text-muted)">
          Nenhuma mensagem neste dia.
        </p>
      ) : (
        <ul className="mt-3 flex flex-col divide-y divide-(--border-subtle)">
          {rows.map((entry) => {
            const status = statusOf(entry)
            return (
              <li
                key={entry.id}
                className="flex flex-wrap items-center justify-between gap-2 py-2"
              >
                <div className="flex min-w-0 flex-col">
                  <span className="text-(length:--text-sm) text-(--text-primary)">
                    {timeOf(entry.sentAt ?? entry.dueAt)} ·{' '}
                    {KIND_LABELS[entry.kind] ?? entry.kind} ·{' '}
                    {entry.customerName}
                  </span>
                  <span className="text-(length:--text-xs) text-(--text-muted)">
                    Venda {entry.saleNumber ?? '—'}
                    {entry.sellerName === null
                      ? ''
                      : ` · vendeu ${entry.sellerName}`}
                    {entry.status === 'skipped' && entry.skipReason !== null
                      ? ` · ${SKIP_LABELS[entry.skipReason] ?? entry.skipReason}`
                      : ''}
                  </span>
                </div>
                <span className="flex items-center gap-2">
                  <Badge tone={status.tone}>{status.label}</Badge>
                  {canManage && entry.status === 'scheduled' ? (
                    <>
                      <Button
                        size="sm"
                        variant="secondary"
                        disabled={act.isPending}
                        onClick={() => {
                          act.mutate({ id: entry.id, action: 'now' })
                        }}
                      >
                        Mandar agora
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={act.isPending}
                        onClick={() => {
                          act.mutate({ id: entry.id, action: 'cancel' })
                        }}
                      >
                        Cancelar
                      </Button>
                    </>
                  ) : null}
                </span>
              </li>
            )
          })}
        </ul>
      )}
    </Section>
  )
}

const ConfigEditor = ({
  config,
  canManage,
  onSaved,
}: {
  config: SaleMessageConfigType
  canManage: boolean
  /** Quem chama mostra o "Salvo.": este editor recomeça com a versão nova. */
  onSaved: () => void
}): ReactNode => {
  const queryClient = useQueryClient()
  const [draft, setDraft] = useState(config)
  const [notice, setNotice] = useState<string | null>(null)
  const set = <K extends keyof SaleMessageConfigType>(
    key: K,
    value: SaleMessageConfigType[K]
  ): void => {
    setDraft((previous) => ({ ...previous, [key]: value }))
  }

  const save = useMutation({
    mutationFn: async () => await saveSaleMessageConfig(draft),
    onSuccess: async (saved) => {
      onSaved()
      queryClient.setQueryData(['sale-message-config'], saved)
      await queryClient.invalidateQueries({ queryKey: ['sale-messages'] })
    },
    onError: (cause: unknown) => {
      setNotice(
        cause instanceof ApiError
          ? cause.message
          : 'Não foi possível salvar agora.'
      )
    },
  })

  const variables = 'Use {cliente}, {vendedora} e {peca} no texto.'
  return (
    <>
      <Section
        title="Saiu para entrega"
        description="Quando o caixa marca a saída com o motoboy, o cliente recebe pelo número de quem vendeu (ou pelo último em que ele conversou)."
      >
        <div className="flex flex-col gap-3">
          <SwitchField
            label="Avisar o cliente que o pedido saiu"
            checked={draft.dispatchedEnabled}
            disabled={!canManage}
            onCheckedChange={(checked) => {
              set('dispatchedEnabled', checked)
            }}
          />
          <Field label="Mensagem" hint={variables}>
            {(field) => (
              <Textarea
                {...field}
                rows={2}
                value={draft.dispatchedText}
                disabled={!canManage}
                onChange={(event) => {
                  set('dispatchedText', event.target.value)
                }}
              />
            )}
          </Field>
        </div>
      </Section>

      <Section
        title="Deu certo a instalação?"
        description="Horas depois da compra, só com a loja aberta. Uma pergunta por cliente por dia. Quem responde que deu certo recebe a oferta; quem teve problema ganha a etiqueta “Pós-venda: problema” e vai para quem vendeu."
      >
        <div className="flex flex-col gap-3">
          <SwitchField
            label="Perguntar depois da compra"
            description="Vale para toda venda com cliente cadastrado (não o Consumidor Final)."
            checked={draft.aftersaleEnabled}
            disabled={!canManage}
            onCheckedChange={(checked) => {
              set('aftersaleEnabled', checked)
            }}
          />
          <Field label="Horas depois da compra" className="sm:w-48">
            {(field) => (
              <Input
                {...field}
                type="number"
                min={1}
                max={72}
                value={String(draft.aftersaleDelayHours)}
                disabled={!canManage}
                onChange={(event) => {
                  const hours = Math.trunc(Number(event.target.value))
                  if (Number.isFinite(hours) && hours > 0) {
                    set('aftersaleDelayHours', hours)
                  }
                }}
              />
            )}
          </Field>
          <Field label="A pergunta" hint={variables}>
            {(field) => (
              <Textarea
                {...field}
                rows={2}
                value={draft.aftersaleCheckText}
                disabled={!canManage}
                onChange={(event) => {
                  set('aftersaleCheckText', event.target.value)
                }}
              />
            )}
          </Field>
          <Field
            label="Quando deu certo"
            hint="A oferta: mais peças, acessórios, a promoção da semana."
          >
            {(field) => (
              <Textarea
                {...field}
                rows={2}
                value={draft.aftersaleOfferText}
                disabled={!canManage}
                onChange={(event) => {
                  set('aftersaleOfferText', event.target.value)
                }}
              />
            )}
          </Field>
          <div>
            <p className="mb-2 text-(length:--text-sm) font-medium text-(--text-primary)">
              Horário em que a pergunta pode sair
            </p>
            <WorkingHoursEditor
              value={draft.aftersaleHours}
              disabled={!canManage}
              onChange={(value) => {
                if (value !== null) set('aftersaleHours', value)
              }}
            />
          </div>
        </div>
      </Section>

      <InlineAlert tone="info">
        O número pareado no celular só manda para quem já conversou com ele. O
        cliente que nunca escreveu para a loja aparece como “Não saiu”.
      </InlineAlert>

      {notice === null ? null : (
        <InlineAlert tone="danger">{notice}</InlineAlert>
      )}

      {canManage ? (
        <div className="flex justify-end">
          <Button
            loading={save.isPending}
            onClick={() => {
              setNotice(null)
              save.mutate()
            }}
          >
            Salvar
          </Button>
        </div>
      ) : null}
    </>
  )
}

export const AtendimentoPosVendaPage = (): ReactNode => {
  const { allows } = useAuth()
  const canManage = allows('communication.followup.manage')
  const [saved, setSaved] = useState(false)
  const config = useQuery({
    queryKey: ['sale-message-config'],
    queryFn: async ({ signal }) => await fetchSaleMessageConfig(signal),
  })

  return (
    <>
      <TabHeader
        title="Pós-venda"
        description="O aviso de saída para entrega e a pergunta “deu certo a instalação?”, pelo número de quem vendeu."
      />
      {config.isPending ? (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      ) : config.isError ? (
        <EmptyState
          icon={<PackageCheck aria-hidden="true" className="h-8 w-8" />}
          title="Não foi possível carregar o pós-venda"
          description="Verifique a conexão com o servidor e tente novamente."
        />
      ) : (
        <div className="flex flex-col gap-4">
          <DayList canManage={canManage} />
          {/* A versão muda ao salvar: o rascunho recomeça do que foi gravado. */}
          <ConfigEditor
            key={config.data.version}
            config={config.data}
            canManage={canManage}
            onSaved={() => {
              setSaved(true)
            }}
          />
          {saved ? <InlineAlert tone="success">Salvo.</InlineAlert> : null}
        </div>
      )}
    </>
  )
}
